import tw from 'twin.macro';
import RepsIcon from '../../assets/svgs/majesticons_repeat-circle.svg';
import QualityIcon from '../../assets/svgs/quality-icon.svg';
import HoldIcon from '../../assets/svgs/plank-timer.svg';

// The three live readouts — rep counter, form score, countdown — one pill each.
// Two layouts share them: portrait mobile lines them up in a band between the
// camera and the reference video (MobileExerciseStatBar, frames 1527:25757 /
// 25781 / 25807), landscape floats them in the two cards' top corners
// (2058-14852). One pill, two scales:
//
//   md  portrait band       padding 12px, radius 12px, icon 28px, value 32px, suffix 18px
//   sm  landscape overlay   padding 8/12px, radius 10px, icon 22px, value 26px, suffix 16px
//
// The scrim is rgba(24,24,24,0.5) at both scales: the value is read against a
// camera frame either way, so it does not scale with the type.
const pillStyle = tw`flex items-center rounded-[12px] bg-[rgba(24,24,24,0.5)] p-3 text-white`;
const pillSmallStyle = tw`w-fit gap-2 rounded-[10px] px-3 py-2`;
const iconStyle = tw`h-7 w-7 shrink-0 object-contain`;
const iconSmallStyle = tw`h-[22px] w-[22px]`;
// tabular-nums so digits don't reflow as values tick. The design's fixed pill
// widths do most of this already; this keeps the glyphs steady inside them.
const valueStyle = tw`text-[32px] font-semibold leading-none tabular-nums [font-family: Inter, sans-serif]`;
const valueSmallStyle = tw`text-[26px]`;
const suffixStyle = tw`text-[18px]`;
const suffixSmallStyle = tw`text-[16px]`;

const isSmallSize = size => size === 'sm';

export const CounterChip = ({ counter, totalReps, isHoldMode = false, size = 'md', chipStyle }) => {
  const isSmall = isSmallSize(size);
  const hasTotalReps = typeof totalReps === 'number' && totalReps > 0;
  // A null counter means offline: nothing is counting reps, so the pill shows the
  // prescribed target alone instead of a live count parked at 0.
  const hasLiveCounter = counter !== null && counter !== undefined;

  return (
    // Hold movements swap the repeat glyph for the stopwatch (frame 25807).
    <div css={[pillStyle, isSmall && pillSmallStyle, chipStyle]}>
      <img
        src={isHoldMode ? HoldIcon : RepsIcon}
        alt=""
        css={[iconStyle, isSmall && iconSmallStyle]}
      />
      <p css={[valueStyle, isSmall && valueSmallStyle, tw`flex items-baseline`]}>
        <span>{hasLiveCounter ? counter : hasTotalReps ? totalReps : '-'}</span>
        {/* "00 /00" — the target reads as secondary to the live count (frame
            25757). Offline there is no live count, so the target is the number
            itself and this suffix is dropped. */}
        {hasLiveCounter && hasTotalReps && (
          <span css={[suffixStyle, isSmall && suffixSmallStyle, tw`ml-0.5 text-white/60`]}>
            /{totalReps}
          </span>
        )}
      </p>
    </div>
  );
};

export const ScoreChip = ({ score, hasScore = true, size = 'md', chipStyle }) => {
  const isSmall = isSmallSize(size);
  // hasScore=false is REP_BASED at 0 completed reps: nothing has been scored
  // yet, so "--" reads as "no data" instead of a misleadingly literal "0%".
  const value = hasScore && typeof score === 'number' ? Math.round(score) : null;

  return (
    <div css={[pillStyle, isSmall && pillSmallStyle, chipStyle]}>
      <img src={QualityIcon} alt="" css={[iconStyle, isSmall && iconSmallStyle]} />
      <p css={[valueStyle, isSmall && valueSmallStyle, tw`flex items-center gap-1`]}>
        <span>{value ?? '-'}</span>
        <span css={[suffixStyle, isSmall && suffixSmallStyle]}>%</span>
      </p>
    </div>
  );
};

// No icon on the countdown pill, and a step smaller than the other two values
// (frame 25781) — the string is wider, so it reads at the same weight.
export const TimerChip = ({ value, size = 'md', chipStyle }) => {
  const isSmall = isSmallSize(size);

  return (
    <div
      css={[pillStyle, tw`justify-center`, isSmall && pillSmallStyle, chipStyle]}
      data-testid="timer"
    >
      <span css={[valueStyle, isSmall ? valueSmallStyle : tw`text-[28px]`]}>{value}</span>
    </div>
  );
};
